import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ZoomIn, ZoomOut, CalendarRange, Target, CornerDownLeft, Home, Tent, Trash2, MoveHorizontal, MoveVertical, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { STATUS, STATUS_ORDER, FLAGS, fmtDate } from "@/lib/constants";
import { gridPositions } from "@/lib/grid";
import { useLang } from "@/i18n";
import { useCamp } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";
import ZoneOverlay from "@/components/dashboard/ZoneOverlay";
import PitchMarker from "@/components/dashboard/PitchMarker";
import PrintDialog from "@/components/dashboard/PrintDialog";
import PrintPlusDialog from "@/components/dashboard/PrintPlusDialog";
import DemoFillSwitch from "@/components/dashboard/DemoFillSwitch";
import { LiveIndicator } from "@/components/dashboard/LiveIndicator";
import PitchRadarTip from "@/components/dashboard/PitchRadarTip";

const MAP_SRC = `${process.env.PUBLIC_URL || ""}/camping-map.png`;
export const mapSrc = (camp) => (camp?.map_image ? `${process.env.PUBLIC_URL || ""}${camp.map_image}` : MAP_SRC);
const NONE = "__none__";
// Aandeel van de kaartbreedte per afmeting: groot dekt een volledig vak
export const SIZE_PCT = { 1: 0.016, 2: 0.024 };
export const SIZES = [1, 2];
export const SHAPES = ["circle", "square", "diamond"];

function LegendRow({ color, label, shape = "circle" }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-3 w-3 ${shape === "square" ? "rounded-[3px]" : "rounded-full"}`} style={{ background: color }} />
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

export default function CampsiteMap({ pitches, stats, guests = [], onUpdate, onReload, editable = false, defaultZoom = null, onSwitchToPlanning = null, highlightLabels = [], handoverLabels = [] }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { camp } = useCamp();
  const [zoom, setZoom] = useState(() => {
    if (defaultZoom) return defaultZoom;
    const saved = Number(localStorage.getItem("campad.map.zoom"));
    return saved >= 0.5 && saved <= 8 ? saved : 1;
  });
  const savedZoom = useRef(!defaultZoom && Number(localStorage.getItem("campad.map.zoom")) >= 0.5);
  const panRestored = useRef(false);
  useEffect(() => {
    if (!editable) localStorage.setItem("campad.map.zoom", String(zoom));
  }, [zoom, editable]);
  const outerRef = useRef(null);
  const [pos, setPos] = useState({});
  const [dragId, setDragId] = useState(null);
  const [movedId, setMovedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState("");
  const [foundId, setFoundId] = useState(null);
  const [hover, setHover] = useState(null);

  // Zoeken op de kaart: plaatsnummer of gastnaam, de kaart springt naar de plaats
  const findPitch = (e) => {
    e.preventDefault();
    const q = query.trim().toLowerCase();
    if (!q) return;
    const hit = placed.find((p) => (p.label || "").toLowerCase() === q)
      || placed.find((p) => (p.label || "").toLowerCase().startsWith(q))
      || placed.find((p) => (p.guest_name || "").toLowerCase().includes(q));
    if (!hit) { toast.error(t("map_search_none")); return; }
    const xy = getXY(hit);
    const el = outerRef.current;
    const inner = innerRef.current;
    if (el && inner && xy) {
      el.scrollTo({
        left: (xy.x / 100) * inner.clientWidth - el.clientWidth / 2,
        top: (xy.y / 100) * inner.clientHeight - el.clientHeight / 2,
        behavior: "smooth",
      });
    }
    setFoundId(hit.id);
    setTimeout(() => setFoundId(null), 4000);
  };
  const [form, setForm] = useState({ status: "vrij", note: "", guest_id: NONE, controle_nodig: false, rood_lintje: false, size: 1 });
  const [mapW, setMapW] = useState(1200);
  const [picked, setPicked] = useState([]); // labels/ids geselecteerd in het bakje
  const lastPick = useRef(null);

  const innerRef = useRef(null);
  const trayRef = useRef(null);
  const overTray = useRef(false);
  const dragRef = useRef(null);
  const panRef = useRef(null);
  const [panning, setPanning] = useState(false);

  const getXY = (p) => pos[p.id] || (p.x != null ? { x: p.x, y: p.y } : null);
  const guestName = (id) => {
    const g = guests.find((x) => x.id === id);
    return g ? `${g.first_name} ${g.last_name}`.trim() : "";
  };  const placed = pitches.filter((p) => getXY(p));
  const unplaced = pitches.filter((p) => !getXY(p));

  const unplace = useCallback(async (id) => {
    try {
      await api.post(`/pitches/${id}/unplace`);
      setPos((prev) => { const next = { ...prev }; delete next[id]; return next; });
      toast.success(t("back_to_tray_done"));
      onReload && onReload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, [onReload, t]);

  const savePos = useCallback(async (id, x, y) => {
    try {
      await api.patch(`/pitches/${id}`, { x, y });
      onReload && onReload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, [onReload]);

  useEffect(() => {
    if (!dragId) return;
    const onMove = (e) => {
      const rect = innerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const x = Math.max(1, Math.min(99, ((cx - rect.left) / rect.width) * 100));
      const y = Math.max(1, Math.min(99, ((cy - rect.top) / rect.height) * 100));
      dragRef.current = { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100, moved: true };
      const tray = trayRef.current?.getBoundingClientRect();
      overTray.current = !!tray && cx >= tray.left && cx <= tray.right && cy >= tray.top && cy <= tray.bottom;
      setPos((prev) => ({ ...prev, [dragId]: dragRef.current }));
    };
    const onUp = async () => {
      const id = dragId;
      const p = dragRef.current;
      setDragId(null);
      if (!p?.moved) return;
      setMovedId(id);
      if (overTray.current) {
        overTray.current = false;
        await unplace(id);
        return;
      }
      await savePos(id, p.x, p.y);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragId, savePos, unplace]);

  useEffect(() => {
    const measure = () => setMapW(innerRef.current?.clientWidth || 1200);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [zoom]);

  // Kaart met de muis verschuiven (slepen op de kaart zelf, niet op een marker)
  const startPan = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return;
    const el = outerRef.current;
    if (!el) return;
    panRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    setPanning(true);
  };

  useEffect(() => {
    if (!panning) return;
    const move = (e) => {
      const el = outerRef.current;
      const p = panRef.current;
      if (!el || !p) return;
      el.scrollLeft = p.sl - (e.clientX - p.x);
      el.scrollTop = p.st - (e.clientY - p.y);
    };
    const up = () => setPanning(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [panning]);

  // Dubbelklik = inzoomen op die plek, shift+dubbelklik = uitzoomen
  const zoomAt = (e) => {
    if (e.target.closest("button")) return;
    const el = outerRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    const rect = inner.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;
    const offX = e.clientX - el.getBoundingClientRect().left;
    const offY = e.clientY - el.getBoundingClientRect().top;
    setZoom((z) => {
      const next = Math.max(0.5, Math.min(8, Math.round(z * (e.shiftKey ? 1 / 1.6 : 1.6) * 100) / 100));
      requestAnimationFrame(() => {
        el.scrollLeft = relX * inner.clientWidth - offX;
        el.scrollTop = relY * inner.clientHeight - offY;
      });
      return next;
    });
  };

  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.5, Math.min(8, Math.round((z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)) * 100) / 100)));
  };
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const fitHeight = useCallback(() => {
    const el = outerRef.current;
    if (!el) return;
    const avail = editable ? window.innerHeight * 0.75 : window.innerHeight - el.getBoundingClientRect().top - 72;
    const fullH = el.clientWidth / (2000 / 1413);
    setZoom(Math.max(0.5, Math.min(8, Math.floor((avail / fullH) * 10) / 10)));
  }, [editable]);
  const fitWidth = () => setZoom(1);

  // Fit-to-screen: initial zoom so the whole map is visible without scrolling
  useEffect(() => {
    if (defaultZoom || savedZoom.current) return;
    const id = setTimeout(fitHeight, 50);
    return () => clearTimeout(id);
  }, [defaultZoom, fitHeight]);

  // Positie op de kaart bewaren en bij het opstarten terugzetten
  useEffect(() => {
    const el = outerRef.current;
    if (!el || editable) return;
    let timer = null;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const inner = innerRef.current;
        if (!inner || !inner.clientWidth) return;
        localStorage.setItem("campad.map.pan", JSON.stringify({
          fx: el.scrollLeft / inner.clientWidth, fy: el.scrollTop / inner.clientHeight,
        }));
      }, 400);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(timer); el.removeEventListener("scroll", onScroll); };
  }, [editable]);

  useEffect(() => {
    if (editable || panRestored.current) return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem("campad.map.pan") || "null"); } catch (e) { saved = null; }
    if (!saved) { panRestored.current = true; return; }
    const id = setTimeout(() => {
      const el = outerRef.current;
      const inner = innerRef.current;
      if (!el || !inner?.clientWidth) return;
      el.scrollLeft = saved.fx * inner.clientWidth;
      el.scrollTop = saved.fy * inner.clientHeight;
      panRestored.current = true;
    }, 250);
    return () => clearTimeout(id);
  }, [editable, zoom]);

  const startDrag = (id, xy) => (e) => {
    if (!editable) return;
    if (e.cancelable && e.type === "mousedown") e.preventDefault();
    dragRef.current = xy ? { ...xy, moved: false } : null;
    setDragId(id);
  };

  const toggleTray = (p, e) => {
    const list = unplaced.map((u) => u.id);
    if (e.shiftKey && lastPick.current) {
      const a = list.indexOf(lastPick.current);
      const b = list.indexOf(p.id);
      if (a > -1 && b > -1) {
        const range = list.slice(Math.min(a, b), Math.max(a, b) + 1);
        setPicked((prev) => Array.from(new Set([...prev, ...range])));
        return;
      }
    }
    lastPick.current = p.id;
    setPicked((prev) => (prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]));
  };

  const placePicked = async (e) => {
    const rect = innerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x0 = ((e.clientX - rect.left) / rect.width) * 100;
    const y0 = ((e.clientY - rect.top) / rect.height) * 100;
    const chosen = unplaced.filter((p) => picked.includes(p.id));
    const cols = Math.min(6, Math.max(1, Math.ceil(Math.sqrt(chosen.length))));
    const spots = gridPositions(x0, y0, chosen.length, SIZE_PCT[chosen[0]?.size] || SIZE_PCT[1], cols);
    try {
      await api.post("/pitches/positions", { items: chosen.map((p, i) => ({ id: p.id, ...spots[i] })) });
      setPicked([]);
      toast.success(t("placed_n").replace("{n}", chosen.length));
      onReload && onReload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openPitch = (p) => {
    if (movedId === p.id) { setMovedId(null); return; }
    setSelected(p);
    setForm({
      status: p.status, note: p.note || "", guest_id: p.guest_id || NONE,
      controle_nodig: !!p.controle_nodig, rood_lintje: !!p.rood_lintje, size: p.size === 2 ? 2 : 1,
      shape: p.shape || (p.kind === "pitch" ? "circle" : "square"),
      oos_until: p.oos_until || "",
    });
  };

  const oosAllowed = selected?.status === "vrij" || selected?.status === "buiten_dienst";
  const oosInvalid = form.status === "buiten_dienst" && !form.note.trim();

  const save = async () => {
    const from = selected.status;
    const to = form.status;
    const label = encodeURIComponent(selected.label);

    // Buiten dienst en terug naar vrij zijn gewone statuswijzigingen, zonder werkflow
    if (to === "buiten_dienst" || from === "buiten_dienst") {
      if (oosInvalid) { toast.error(t("oos_note_required")); return; }
      await onUpdate(selected.id, {
        status: to, note: form.note, controle_nodig: form.controle_nodig,
        oos_until: to === "buiten_dienst" ? (form.oos_until || "") : "",
      });
      setSelected(null);
      return;
    }

    // Op het overzicht leidt een statuswijziging normaal naar de juiste werkflow i.p.v. de status zelf
    // te zetten. In DEMO-mode zetten we de status rechtstreeks (geen aankomst/vertrek-werkflow).
    if (!editable && !camp?.demo_mode && from !== to) {
      await onUpdate(selected.id, { note: form.note, controle_nodig: form.controle_nodig });
      let stay = null;
      try { stay = (await api.get(`/pitches/${selected.id}/stay`)).data?.stay; }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); return; }

      if (to === "vrij" && from === "gereserveerd") {
        if (!stay?.reservation_id) { toast.error(t("no_reservation_found")); return; }
        setConfirmDelete({ id: stay.reservation_id, name: stay.guest_name || selected.label });
        return;
      }
      setSelected(null);
      if (to === "bezet") navigate(stay?.reservation_id ? `/reservaties?id=${stay.reservation_id}` : `/reservaties?pitch=${label}`);
      else if (to === "vrij") navigate(stay?.reservation_id ? `/vertrekkend?id=${stay.reservation_id}` : `/vertrekkend?pitch=${label}`);
      else if (to === "gereserveerd") navigate(`/reservaties?pitch=${label}`);
      return;
    }

    await onUpdate(selected.id, {
      status: form.status, note: form.note, controle_nodig: form.controle_nodig, size: form.size, shape: form.shape,
      guest_id: form.guest_id === NONE ? "" : form.guest_id,
    });
    setSelected(null);
  };

  const deleteReservation = async () => {
    try {
      await api.delete(`/reservations/${confirmDelete.id}`);
      toast.success(t("reservation_removed_guest_kept"));
      setConfirmDelete(null);
      setSelected(null);
      onReload && onReload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removePitch = async () => {
    try {
      await api.delete(`/pitches/${selected.id}`);
      toast.success(t("deleted"));
      setSelected(null);
      onReload && onReload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const isPitch = !!selected;
  // Niet-demo overzicht: een statuswissel start een werkflow -> knop toont "Doorgaan" i.p.v. "Opslaan".
  const willWorkflow = !editable && !camp?.demo_mode && !!selected && form.status !== selected.status
    && form.status !== "buiten_dienst" && selected.status !== "buiten_dienst";

  return (
    <div>
      <div className="px-5 pt-4 flex flex-wrap items-center gap-2 mb-3">
        {editable ? (
          <span className="text-xs text-muted-foreground">{t("editing_layout")}</span>
        ) : (
          <>
            {onSwitchToPlanning && (
              <Button variant="outline" size="sm" className="h-8" onClick={onSwitchToPlanning}
                title={t("toggle_view_hint")} data-testid="switch-to-planning">
                <CalendarRange className="h-4 w-4 mr-1" />{t("view_planning")}
              </Button>
            )}
            <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))} data-testid="zoom-out" title={t("zoom_out")}><ZoomOut className="h-4 w-4" /></Button>
              <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-center" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => setZoom((z) => Math.min(8, Math.round((z + 0.1) * 10) / 10))} data-testid="zoom-in" title={t("zoom_in")}><ZoomIn className="h-4 w-4" /></Button>
              <span className="h-5 w-px bg-border" />
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={fitWidth} data-testid="fit-width" title={t("fit_width")}><MoveHorizontal className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={fitHeight} data-testid="fit-height" title={t("fit_height")}><MoveVertical className="h-4 w-4" /></Button>
            </div>
            <PrintDialog />
            <PrintPlusDialog />
            <DemoFillSwitch onReload={onReload} />
            <form onSubmit={findPitch} className="relative flex items-center" data-testid="map-search-form">
              <button type="submit" title={t("map_search_btn")} data-testid="map-search-button"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                <Target className="h-4 w-4" />
              </button>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="map-search-input"
                placeholder={t("map_search_ph")} className="h-9 w-48 sm:w-60 rounded-full pl-9" />
            </form>
            <div className="ml-auto flex items-center gap-2">
              <LiveIndicator />
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" aria-label={t("map_help")} data-testid="map-help-button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/40 transition-colors">
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="max-w-xs w-auto bg-blue-600 text-white border-blue-700 p-3 text-xs leading-relaxed" data-testid="map-help-popover">
                  {t("map_hint")} · {t("pan_hint")} · {t("zoom_hint")} · {t("dblclick_hint")}
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>

      <div
        ref={outerRef}
        onMouseDown={startPan}
        onClick={(e) => { if (editable && picked.length && !e.target.closest("button")) placePicked(e); }}
        onDoubleClick={zoomAt}
        className={`relative mx-5 mb-5 overflow-auto cb-scroll rounded-lg border border-border bg-muted/40 ${panning ? "cursor-grabbing" : "cursor-grab"} ${editable ? "max-h-[75vh]" : "h-[calc(100vh-20.5rem)] min-h-[280px]"}`}
        data-testid="campsite-map"
      >
        <div ref={innerRef} className="relative select-none mx-auto" style={{ width: `${zoom * 100}%`, aspectRatio: "2000 / 1413" }}>
          <img src={mapSrc(camp)} alt="Plattegrond" className="absolute inset-0 h-full w-full object-contain pointer-events-none" draggable={false} />

          <ZoneOverlay stats={stats} scale={zoom} />

          {placed.map((p) => {
            const xy = getXY(p);
            return (
              <PitchMarker
                key={p.id} p={p} x={xy.x} y={xy.y} lang={lang} editable={editable}
                px={Math.max(12, mapW * (SIZE_PCT[p.size] || SIZE_PCT[1]))}
                highlight={foundId === p.id || highlightLabels.includes(p.label)}
                handover={handoverLabels.includes(p.label)} handoverText={t("handover_today")}
                onMouseEnter={!editable ? (e) => setHover({ pitch: p, x: e.clientX, y: e.clientY }) : undefined}
                onMouseMove={!editable ? (e) => setHover({ pitch: p, x: e.clientX, y: e.clientY }) : undefined}
                onMouseLeave={!editable ? () => setHover(null) : undefined}
                onMouseDown={startDrag(p.id, xy)} onTouchStart={startDrag(p.id, xy)}
                onClick={() => openPitch(p)}
              />
            );
          })}

        </div>
      </div>

      {editable && (
        <div ref={trayRef} data-testid="unplaced-tray"
          className={`mt-3 rounded-lg border border-dashed px-4 py-3 transition-colors ${dragId ? "border-primary bg-primary/10" : "border-primary/50 bg-secondary/40"}`}>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("unplaced_pitches")} ({unplaced.length})</p>
            <p className="text-[11px] text-muted-foreground">{t("multi_select_hint")}</p>
            <p className="text-[11px] font-semibold text-primary">{t("drop_here_hint")}</p>
            {picked.length > 0 && (
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-[11px] font-semibold" data-testid="picked-count">
                {t("selected_n").replace("{n}", picked.length)}
                <button onClick={() => setPicked([])} className="underline" data-testid="clear-picked">{t("cancel")}</button>
              </span>
            )}
          </div>
          {unplaced.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("unplaced_empty")}</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {unplaced.map((p) => (
                <div key={p.id} className={`flex flex-col items-center w-10 rounded-md ${picked.includes(p.id) ? "ring-2 ring-primary bg-primary/10" : ""}`} data-testid={`tray-${p.label}`}>
                  <div className="relative h-8 w-8">
                    <PitchMarker p={p} x={50} y={50} lang={lang} editable px={26}
                      onMouseDown={(e) => { if (!(e.ctrlKey || e.metaKey || e.shiftKey)) startDrag(p.id, null)(e); }}
                      onTouchStart={startDrag(p.id, null)}
                      onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey) toggleTray(p, e); else openPitch(p); }} />
                  </div>
                  <span className="text-[9px] leading-none text-muted-foreground">{p.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 bg-card border border-border rounded-lg px-4 py-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("legend")}</span>
        {STATUS_ORDER.map((s) => <LegendRow key={s} color={STATUS[s].color} label={STATUS[s][lang] || STATUS[s].nl} />)}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full border-2" style={{ borderColor: FLAGS.rood_lintje.color }} />
          <span className="text-[11px] text-muted-foreground">{FLAGS.rood_lintje[lang] || FLAGS.rood_lintje.nl}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ background: FLAGS.controle_nodig.color }} />
          <span className="text-[11px] text-muted-foreground">{FLAGS.controle_nodig[lang] || FLAGS.controle_nodig.nl}</span>
        </div>
        {handoverLabels.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="legend-handover">
            <span className="grid h-3 w-3 place-items-center rounded-full bg-amber-400 ring-2 ring-white"><span className="h-1 w-1 rounded-full bg-amber-700" /></span>
            <span className="text-[11px] text-muted-foreground">{t("handover_today")}</span>
          </div>
        )}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <LegendRow color="#0ea5e9" shape="square" label={t("accommodation")} />
      </div>

      <Dialog open={!!isPitch} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pitch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {selected?.kind === "pitch" ? <Tent className="h-4 w-4" /> : <Home className="h-4 w-4" />}
              {t("pitch")} {selected?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">{t("status")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {STATUS_ORDER.map((s) => (
                  <button key={s} onClick={() => setForm({ ...form, status: s })} data-testid={`status-option-${s}`}
                    disabled={s === "buiten_dienst" && !oosAllowed}
                    title={s === "buiten_dienst" && !oosAllowed ? t("oos_only_from_free") : undefined}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${form.status === s ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                    <span className="h-3 w-3 rounded-sm" style={{ background: STATUS[s].color }} />
                    {STATUS[s][lang] || STATUS[s].nl}
                  </button>
                ))}
              </div>
            </div>

            {form.status === "buiten_dienst" && (
              <div>
                <Label htmlFor="oosuntil" className="mb-2 block">{t("oos_until")}</Label>
                <Input id="oosuntil" type="date" value={form.oos_until || ""} data-testid="pitch-oos-until"
                  onChange={(e) => setForm({ ...form, oos_until: e.target.value })} />
                <p className="mt-1 text-[11px] text-muted-foreground">{t("oos_until_hint")}</p>
              </div>
            )}

            {(selected?.status === "bezet" || selected?.status === "gereserveerd") && (
              <div>
                <Label className="mb-2 block">{t("occupied_by")}</Label>
                {editable ? (
                  <Select value={form.guest_id} onValueChange={(v) => setForm({ ...form, guest_id: v })}>
                    <SelectTrigger data-testid="pitch-guest-select"><SelectValue placeholder={t("assign_guest")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t("none_selected")}</SelectItem>
                      {guests.map((g) => <SelectItem key={g.id} value={g.id}>{g.first_name} {g.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm" data-testid="pitch-guest-readonly">
                    {guestName(form.guest_id) || selected?.guest_name || t("none_selected")}
                  </p>
                )}
              </div>
            )}

            {selected?.rood_lintje && (
              <div className="rounded-lg border px-3 py-2 text-sm flex items-center gap-2" style={{ borderColor: FLAGS.rood_lintje.color, background: FLAGS.rood_lintje.color + "14" }} data-testid="ribbon-info">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: FLAGS.rood_lintje.color }} />
                <span><b>{FLAGS.rood_lintje[lang] || FLAGS.rood_lintje.nl}</b> · {selected.rood_lintje_guest} · {selected.rood_lintje_active ? `${t("until")} ${fmtDate(selected.rood_lintje_to)}` : `${t("free_until")} ${fmtDate(selected.rood_lintje_from)}`}</span>
              </div>
            )}
            <div>
              <Label className="mb-2 block">{t("flags")}</Label>
              <div className="flex flex-wrap gap-2">
                {["controle_nodig"].map((f) => (
                  <button key={f} onClick={() => setForm({ ...form, [f]: !form[f] })} data-testid={`flag-${f === "controle_nodig" ? "controle" : "rood"}`}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${form[f] ? "text-white" : "border-border hover:bg-muted"}`}
                    style={form[f] ? { background: FLAGS[f].color, borderColor: FLAGS[f].color } : {}}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: form[f] ? "#fff" : FLAGS[f].color }} />
                    {FLAGS[f][lang] || FLAGS[f].nl}
                  </button>
                ))}
              </div>
            </div>

            {editable && (<>
            <div>
              <Label className="mb-2 block">{t("shape")}</Label>
              <div className="flex flex-wrap gap-2">
                {SHAPES.map((sh) => (
                  <button key={sh} onClick={() => setForm({ ...form, shape: sh })} data-testid={`pitch-shape-${sh}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${form.shape === sh ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                    <span className={`block h-3.5 w-3.5 bg-slate-600 ${sh === "circle" ? "rounded-full" : "rounded-[2px]"} ${sh === "diamond" ? "rotate-45" : ""}`} />
                    {t(`shape_${sh}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">{t("size")}</Label>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((sz) => (
                  <button key={sz} onClick={() => setForm({ ...form, size: sz })} data-testid={`pitch-size-${sz}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${form.size === sz ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`}>
                    <span className="rounded-full bg-slate-500" style={{ height: 8 + sz * 4, width: 8 + sz * 4 }} />
                    {t(`size_${sz}`)}
                  </button>
                ))}
              </div>
            </div>
            </>)}

            <div className="pb-2">
              <Label htmlFor="pnote" className="mb-2 block">
                {t("note")}{form.status === "buiten_dienst" && <span className="text-rose-500"> *</span>}
              </Label>
              {oosInvalid && (
                <p className="mb-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-300" data-testid="oos-note-error">
                  {t("oos_note_required")}
                </p>
              )}
              <Textarea id="pnote" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pitch-note-input" />
            </div>
          </div>
          <DialogFooter className="sm:justify-between gap-2 flex-wrap">
            {editable ? (
              <Button variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" disabled={selected?.status !== "vrij"} onClick={removePitch} data-testid="pitch-delete-button" title={selected?.status !== "vrij" ? t("delete_only_free") : ""}>
                <Trash2 className="h-4 w-4 mr-1" />{t("delete")}
              </Button>
            ) : <span />}
            <div className="flex flex-wrap gap-2 justify-end">
              {editable && selected?.id && getXY(selected) && (
                <Button variant="outline" onClick={async () => { const id = selected.id; setSelected(null); await unplace(id); }} data-testid="pitch-unplace-button">
                  <CornerDownLeft className="h-4 w-4 mr-1" />{t("back_to_tray")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelected(null)}>{t("cancel")}</Button>
              <Button onClick={save} disabled={oosInvalid} data-testid="pitch-save-button">{willWorkflow ? t("pitch_continue") : t("save")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PitchRadarTip hover={hover} />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>        <AlertDialogContent data-testid="confirm-remove-reservation">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("remove_reservation_q")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("remove_reservation_hint").replace("{name}", confirmDelete?.name || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-remove-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteReservation} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-remove-ok">
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
